/**
 * ConsumptionCertificate flow DTO + completeness predicate (PF v2 native module).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|consumption_certificate"]` (MODULE-CONTRACT
 * Pattern A). The base fields are IDENTICAL to what the legacy adapter serialized
 * (`getConsumptionCertificateRequestData` → `ConsumptionCertificateDetailsPayloadDTO`),
 * so a draft saved under the old adapter round-trips byte-identical (§6 DONE gate).
 *
 * Three ADDITIVE, optional, FE-only (draft-only) fields extend the payload DTO so
 * the pure completeness predicate (§3) can gate WITHOUT reading live hub/context
 * state. They are absent on legacy drafts → treated as default, and are written
 * ONLY on user interaction (never on mount) so the byte-identical round-trip holds.
 * The future B2 submit serializer MUST strip them (DF, owner BE/contract).
 * `PF_VERSION` stays FROZEN at 3 — additive optional fields, no shape change.
 *
 * ZERO React / store imports — `modules/completeness.ts` (which `store/selectors.ts`
 * imports) pulls `consumptionCertificateIsComplete` from here, so this file must
 * stay pure. Enum runtime values come from `constants/purchaseFlow/*` (pure enum
 * files, zero `PurchaseFlow/*` coupling → contract §8 ALLOWED; verified no
 * DF-7-style stranding).
 */

import type {
  ConsumptionCertificateDetailsPayloadDTO,
  ConsumptionCertificateHeaterPayloadDTO,
  HeaterBillingPeriodPayloadDTO,
  ConsumptionCertificateVacancyDTO,
  EnergeticRenovationsDTO
} from 'models/purchaseFlow/consumptionCertificateDetailsDTO'
import {
  ConsumptionCertificateLivingUnits,
  ConsumptionCertificateBasementType,
  ConsumptionCertificateVentilationType,
  RenewableEnergySource,
  RenewableEnergySourceUsage,
  FuelType,
  HotWaterGenerationType
} from 'constants/purchaseFlow/consumptionCertificateTypes'
import { BuildingType, EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'

// Re-export the payload sub-types the codec / UI consume, so downstream module
// files import them from here (one module-local surface) rather than reaching
// into `models/*` in a dozen places.
export type {
  ConsumptionCertificateDetailsPayloadDTO,
  ConsumptionCertificateHeaterPayloadDTO,
  HeaterBillingPeriodPayloadDTO,
  ConsumptionCertificateVacancyDTO,
  EnergeticRenovationsDTO
}

/**
 * Persisted/wire shape = the legacy payload DTO + three additive optional fields.
 */
export interface ConsumptionCertificateConfigDTO extends ConsumptionCertificateDetailsPayloadDTO {
  /**
   * ADDITIVE. The pre-1978 construction-confirmation flag. Legacy computed it from
   * live state (`isShowConstructionNote && isConstructionConfirmed`) and it gated
   * `isPropertyInformationValid` + fed the `energeticRenovations` re-derivation, but
   * it was NEVER on the wire. Persist it so the pure predicate + codec stay faithful.
   * Absent on legacy drafts → treated as `false` (matches a fresh legacy mount).
   */
  isConstructionConfirmed?: boolean
  /** ADDITIVE. Exterior-view upload marker (single file). Kept in sync from the hub
   *  projection by the interaction-gated + value-guarded effect (§4b). */
  exteriorViewFilenames?: string[]
  /** ADDITIVE. Heater-nameplate upload marker (single file). Same sync as above. */
  heaterNameplateFilenames?: string[]
}

/**
 * Read-time fallback for the ops-hook field-merge. NEVER written on mount
 * (read-only-mount invariant, MODULE-CONTRACT §3). Mirrors the legacy sub-context
 * defaults so an empty/undefined persisted entry renders exactly like a fresh
 * legacy mount (residentialUnit 1, NO_BASEMENT, STANDARD_WINDOW_VENTILATION,
 * NONE renewables, empty energetic set, vacant-by-default, NEW_RENTAL reason).
 *
 * `buildingType` / `buildingLocation` are intentionally `undefined` (null-equivalent)
 * — the view renders a placeholder and the predicate gates on them.
 */
export const CONSUMPTION_CERTIFICATE_DEFAULT: ConsumptionCertificateConfigDTO = {
  livingUnitsDescr: ConsumptionCertificateLivingUnits.LESS_THAN_FIVE,
  buildingPeriod: undefined,
  energeticRenovations: { hasDoubleGlazedWindows: false, hasRoofInsulation: false, hasExteriorWallInsulation: false },
  currentEnergeticConditions: [],
  buildingType: undefined as unknown as BuildingType,
  typeOfUseCommercial: '',
  commercialSpaceInSquareMeters: 0,
  buildingLocation: undefined as unknown as ConsumptionCertificateConfigDTO['buildingLocation'],
  buildingYear: 0,
  renovationYear: 0,
  livingUnits: 1,
  totalLivingArea: 0,
  renewableEnergies: RenewableEnergySource.NONE,
  renewableEnergiesUsage: RenewableEnergySourceUsage.NONE,
  ventilation: ConsumptionCertificateVentilationType.STANDARD_WINDOW_VENTILATION,
  basement: ConsumptionCertificateBasementType.NO_BASEMENT,
  heaters: [],
  // Legacy starts this false (`hasPropertyVacant`, ConsumptionCertificateStep.context.ts:174)
  // and so must this. `isConsumptionInformationValid` demands a vacancy PERIOD once
  // the flag is set, so defaulting it true made an untouched form incomplete with
  // nothing on screen saying why — Save simply stayed disabled.
  vacancy: { vacancyPresent: false, vacancyPeriods: [] },
  reasonForEnergyPass: EnergyPassReason.NEW_RENTAL,
  messageToExpert: '',
  isConstructionConfirmed: false,
  exteriorViewFilenames: [],
  heaterNameplateFilenames: [],
}

/**
 * Section predicates — faithful ports of the four legacy validity predicates
 * (`ConsumptionCertificateStep.context.ts:58-107`), re-derived over the persisted
 * DTO + additive fields. Reads NO live hub/context state (§4b). Exposed
 * individually so the chip strip can badge per section; composed into the overall
 * `consumptionCertificateIsComplete` below (the `FLOW_IS_COMPLETE` entry).
 *
 * Faithfulness notes (each a DF — legacy behaviour can't be reproduced 1:1 over the
 * normalized DTO):
 *  - `constructionYear.length===4` (a string-length check on live input) becomes a
 *    4-digit numeric-year check over `buildingYear` — equivalent for real years.
 *  - `consumption`/`primaryEnergyFactor` looseness: legacy `!== null` on never-null
 *    strings; the DTO stores numbers (`Number('')===0`, `0 != null`) → the SAME
 *    looseness is reproduced (empty consumption passes gating).
 *  - `isConstructionConfirmed` is only faithful because we persist it (additive).
 *  - HEATING adds a `heaters.length` guard the raw legacy predicate lacked: legacy
 *    always carried ≥1 (default) heater so `.every()` over an empty array never
 *    arose; a native fresh DTO has `heaters: []` (no mount write) → without the
 *    guard `[].every()` would vacuously pass. The guard keeps the gate
 *    legacy-faithful (an unfilled default heater blocks). Flagged to PM as a
 *    faithfulness fix (never rejects a real legacy draft — serialize always emits
 *    ≥1 heater).
 */
export const isPropertyInformationValid = (dto: ConsumptionCertificateConfigDTO): boolean => {
  const residentialUnit = dto.livingUnits ?? 1
  const isPre1978Note =
    residentialUnit < 5 && (dto.buildingYear ?? 0) < 1978 && String(dto.buildingYear ?? '').length === 4
  if (isPre1978Note && !dto.isConstructionConfirmed) return false
  if (!dto.buildingType || !dto.buildingLocation) return false
  if (
    dto.buildingType === BuildingType.MIXED_USE_BUILDING &&
    (!dto.commercialSpaceInSquareMeters || !dto.typeOfUseCommercial)
  ) return false
  if (!dto.buildingYear || String(dto.buildingYear).length !== 4) return false
  if (dto.renovationYear && String(dto.renovationYear).length !== 4) return false
  if (!dto.totalLivingArea) return false
  return true
}

/** Earliest year a billing or vacancy period may start in; anything older is a typo (QA saw 1011). */
export const PERIOD_MIN_YEAR = 1900

/** `DD.MM.YYYY` → UTC epoch ms, or null for a malformed or impossible date. */
const parseBillingDate = (value: string | undefined): number | null => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value ?? '')
  if (!match) return null
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const time = Date.UTC(year, month - 1, day)
  const date = new Date(time)
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? time : null
}

/**
 * A billing or vacancy period: two real dates, not ending before it starts (the backend allows a one-day
 * period), between 1900 and the end of the current year. Anything else is an unparseable date or a typo
 * the backend rejects or stores as nonsense.
 */
export const isPeriodRangeValid = (start: string | undefined, end: string | undefined, now: Date = new Date()): boolean => {
  const startTime = parseBillingDate(start)
  const endTime = parseBillingDate(end)
  if (startTime == null || endTime == null) return false
  return startTime <= endTime
    && new Date(startTime).getUTCFullYear() >= PERIOD_MIN_YEAR
    && new Date(endTime).getUTCFullYear() <= now.getFullYear()
}

export const isHeatingSystemValid = (dto: ConsumptionCertificateConfigDTO): boolean => {
  if (!(dto.heaters?.length)) return false // faithfulness guard — see doc note
  return dto.heaters.every((h) =>
    h.fuel !== undefined &&
    h.hotWater !== undefined &&
    h.unit !== undefined &&
    // Legacy `isHeatingSystemValid` hard-requires the generator year (`yearOfManufacture !== null`,
    // a nullable Moment). The native encoder maps an unset year to the number 0, so a `!= null` check
    // would let a yearless heater pass — a gating regression (verify-consumption MUST-FIX). Truthy
    // check rejects 0/NaN; a complete legacy draft always has year > 0 so no resumed draft is wrongly blocked.
    !!h.yearOfConstruction &&
    (h.billingPeriod ?? []).every((b) =>
      isPeriodRangeValid(b.startBillingPeriod, b.endOfBillingPeriod) &&
      b.consumption != null &&
      // The encoder stores an empty field as 0, and the backend rejects 0 as well as null (400 for the whole order).
      (h.hotWater !== HotWaterGenerationType.KNOW_HOT_WATER_CONSUMPTION_IN_KWH || (b.hotWaterConsumptionKwh ?? 0) > 0)
    ) &&
    (h.fuel !== FuelType.DISTRICT_HEATING || h.primaryEnergyFactor != null)
  )
}

export const isConsumptionInformationValid = (dto: ConsumptionCertificateConfigDTO): boolean => {
  if (dto.vacancy?.vacancyPresent) {
    const p = dto.vacancy.vacancyPeriods?.[0]
    // A half-typed date is stored as the text "Invalid date", which the backend cannot parse.
    if (!p || !isPeriodRangeValid(p.startVacancy, p.endVacancy) || !p.areaVacancyInSquareMeters) return false
  }
  if (
    dto.renewableEnergies !== RenewableEnergySource.NONE &&
    dto.renewableEnergiesUsage === RenewableEnergySourceUsage.NONE
  ) return false
  return true
}

export const isAdditionalInformationValid = (dto: ConsumptionCertificateConfigDTO): boolean =>
  !!dto.exteriorViewFilenames?.length && !!dto.heaterNameplateFilenames?.length

/**
 * Pure overall completeness predicate (the `FLOW_IS_COMPLETE` entry). Robust to a
 * partial or `undefined` persisted DTO. Untouched flow → `undefined` → not complete
 * (legacy: uploads missing → false).
 */
export const consumptionCertificateIsComplete = (dto: ConsumptionCertificateConfigDTO | undefined): boolean => {
  if (!dto) return false
  return (
    isPropertyInformationValid(dto) &&
    isHeatingSystemValid(dto) &&
    isConsumptionInformationValid(dto) &&
    isAdditionalInformationValid(dto)
  )
}
